// Omnix/client/ui/MetaWebhookWizard.js
import { ComponentBase } from "../core/ComponentBase.js";

// Configuration for the dynamic parts of the wizard
const channelUIConfig = {
    whatsapp_business_api: {
        title: "WhatsApp Business API Setup",
        step2Title: "Step 2: Enter WhatsApp API Credentials",
        fields: [
            { id: 'name', label: 'Account Name', type: 'text', placeholder: 'e.g., Main Business Number' },
            { id: 'phoneNumberId', label: 'Phone Number ID', type: 'text' },
            { id: 'wabaId', label: 'WhatsApp Business Account ID', type: 'text' },
            { id: 'accessToken', label: 'System User Access Token', type: 'textarea', rows: 3 },
            { id: 'appSecret', label: 'App Secret', type: 'password' }
        ]
    },
    facebook_messenger: {
        title: "Facebook Messenger Setup",
        step2Title: "Step 2: Enter Facebook Page Credentials",
        fields: [
            { id: 'name', label: 'Channel Name', type: 'text', placeholder: 'e.g., Main Company Page' },
            { id: 'pageId', label: 'Facebook Page ID', type: 'text' },
            { id: 'accessToken', label: 'Page Access Token', type: 'textarea', rows: 4 },
            { id: 'appSecret', label: 'App Secret', type: 'password' }
        ]
    },
    instagram: {
        title: "Instagram Direct Setup",
        step2Title: "Step 2: Enter Instagram Credentials",
        fields: [
            { id: 'name', label: 'Channel Name', type: 'text', placeholder: 'e.g., Main Instagram Account' },
            { id: 'pageId', label: 'Linked Facebook Page ID', type: 'text' },
            { id: 'accessToken', label: 'Access Token', type: 'textarea', rows: 4 },
            { id: 'appSecret', label: 'App Secret', type: 'password' }
        ]
    }
};

export class MetaWebhookWizard extends ComponentBase {
    static styles = `
        .meta-wizard-modal .modal-dialog { max-width: 700px; }
        .wizard-step-content { display: none; }
        .wizard-step-content.active { display: block; }
        .webhook-info-box {
            background-color: var(--bs-tertiary-bg);
            border: 1px solid var(--bs-border-color);
            border-radius: var(--bs-border-radius);
            padding: 1rem;
        }
        .webhook-info-box code { word-break: break-all; }
        .webhook-status-indicator { font-weight: 500; }
        .webhook-status-indicator.not-verified { color: var(--bs-warning-text-emphasis); }
        .webhook-status-indicator.verified { color: var(--bs-success-text-emphasis); }
    `;

    constructor(config) {
        super(config);
        this.modalInstance = null;
        this.currentStep = 1;
        this.setupId = null;
        this.isWebhookVerified = false;
        this.providerName = config.providerName;
        this.uiConfig = channelUIConfig[this.providerName];
        this._boundWebhookVerifiedHandler = this.handleWebhookVerified.bind(this);
        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'modal fade meta-wizard-modal';
        this.container.id = `${this.config.instanceId}-modal`;
        this.container.setAttribute('tabindex', '-1');

        // Dynamically generate Step 2 fields
        const step2FieldsHtml = this.uiConfig.fields.map(field => `
            <div class="mb-3">
                <label class="form-label">${this.t(field.label)}</label>
                ${field.type === 'textarea'
                ? `<textarea id="meta-${field.id}" class="form-control" rows="${field.rows || 3}"></textarea>`
                : `<input type="${field.type}" id="meta-${field.id}" class="form-control" placeholder="${this.t(field.placeholder || '')}">`
            }
            </div>
        `).join('');

        this.container.innerHTML = `
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">${this.t(this.uiConfig.title)}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <!-- Step 1: Webhook Setup -->
                        <div id="wizard-step-1" class="wizard-step-content">
                            <h6>Step 1: Configure Webhook in Meta for Developers</h6>
                            <p class="text-muted">Copy these values into your Meta App's webhook configuration page. The wizard will proceed once Meta verifies the URL.</p>
                            <div class="webhook-info-box">
                                <div class="mb-3"><label class="form-label small">Callback URL</label><div class="input-group input-group-sm"><input type="text" class="form-control" id="webhook-url-display" readonly><button class="btn btn-outline-secondary" type="button" id="copy-url-btn"><i class="bi bi-clipboard"></i></button></div></div>
                                <div><label class="form-label small">Verify Token</label><div class="input-group input-group-sm"><input type="text" class="form-control" id="webhook-token-display" readonly><button class="btn btn-outline-secondary" type="button" id="copy-token-btn"><i class="bi bi-clipboard"></i></button></div></div>
                            </div>
                            <div class="mt-3 text-center"><span class="webhook-status-indicator not-verified"><span class="spinner-border spinner-border-sm me-2"></span>Waiting for verification...</span></div>
                        </div>
                        <!-- Step 2: API Credentials -->
                        <div id="wizard-step-2" class="wizard-step-content">
                            <h6>${this.t(this.uiConfig.step2Title)}</h6>
                            ${step2FieldsHtml}
                            
                            <h6 class="mt-4">Routing</h6>
                            <div class="mb-3">
                                <label for="meta-workflow-select" class="form-label">Inbound Workflow</label>
                                <select id="meta-workflow-select" class="form-select">
                                    <option value="">Default (Direct to Inbox)</option>
                                </select>
                                <div class="form-text">Select a workflow to handle incoming messages. Workflows must be tagged with 'messaging'.</div>
                            </div>
                    </div>

                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" class="btn btn-primary" id="wizard-next-btn" disabled>Next</button>
                        <button type="button" class="btn btn-success" id="wizard-finish-btn" style="display: none;">Finish Setup</button>
                    </div>
                </div>
            </div>
        `;
        this.modalInstance = new bootstrap.Modal(this.container);
        this.appClient.events.server.on('meta:webhookVerified', this._boundWebhookVerifiedHandler);
        this.attachEventListeners();
        await super.initialize();
    }

    generateMongoObjectId() {
        const timestamp = (new Date().getTime() / 1000 | 0).toString(16);
        return timestamp + 'xxxxxxxxxxxxxxxx'.replace(/[x]/g, () => (Math.random() * 16 | 0).toString(16)).toLowerCase();
    }

    async show() {
        this.reset();
        await this.startStep1();
        this.modalInstance.show();
        // We populate workflows when the modal is shown, as it's a common action for all Meta types.
        await this._populateWorkflows();
    }

    async _populateWorkflows() {
        const select = this.container.querySelector('#meta-workflow-select');
        if (!select) return;
        try {
            const workflows = await this.appClient.rpcClient.call('ChannelManagerRpc.getFieldValues', {
                fieldName: 'inboundWorkflowId',
                reference: {
                    collection: 'workflows', valueField: '_id', displayField: 'name',
                    filter: { tags: 'messaging' }
                }
            });
            select.innerHTML = '<option value="">Default (Direct to Inbox)</option>';
            workflows.forEach(wf => {
                const option = document.createElement('option');
                option.value = wf.value;
                option.textContent = wf.label;
                select.appendChild(option);
            });
        } catch (error) {
            this.log.error("Failed to populate workflows in MetaWebhookWizard:", error);
        }
    }


    reset() {
        this.currentStep = 1;
        this.setupId = null;
        this.isWebhookVerified = false;
        this.container.querySelectorAll('input, textarea').forEach(el => el.value = '');
        this.updateWizardState();
    }

    async startStep1() {
        this.setupId = this.generateMongoObjectId();
        try {
            const result = await this.appClient.rpcClient.call('ChannelManagerRpc.setupProvider', {
                providerName: this.providerName,
                command: 'wizard_start',
                args: { setupId: this.setupId }
            });
            this.container.querySelector('#webhook-url-display').value = result.webhookUrl;
            this.container.querySelector('#webhook-token-display').value = result.verifyToken;
        } catch (error) {
            this.appClient.notifier.error(`Could not start setup: ${error.message}`);
            this.modalInstance.hide();
        }
    }

    handleWebhookVerified({ setupId }) {
        if (this.setupId === setupId) {
            this.isWebhookVerified = true;
            this.appClient.notifier.success("Webhook successfully verified by Meta!");
            this.updateWizardState();
        }
    }

    updateWizardState() {
        this.container.querySelectorAll('.wizard-step-content').forEach(el => el.classList.remove('active'));
        this.container.querySelector(`#wizard-step-${this.currentStep}`).classList.add('active');
        const nextBtn = this.container.querySelector('#wizard-next-btn');
        const finishBtn = this.container.querySelector('#wizard-finish-btn');
        if (this.currentStep === 1) {
            nextBtn.style.display = 'inline-block';
            finishBtn.style.display = 'none';
            nextBtn.disabled = !this.isWebhookVerified;
            const statusIndicator = this.container.querySelector('.webhook-status-indicator');
            if (this.isWebhookVerified) {
                statusIndicator.classList.replace('not-verified', 'verified');
                statusIndicator.innerHTML = `<i class="bi bi-check-circle-fill me-2"></i> Webhook Verified!`;
            } else {
                statusIndicator.classList.replace('verified', 'not-verified');
                statusIndicator.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span> Waiting for verification...`;
            }
        } else if (this.currentStep === 2) {
            nextBtn.style.display = 'none';
            finishBtn.style.display = 'inline-block';
        }
    }

    attachEventListeners() {
        if (this.container.dataset.eventsAttached) return;
        this.container.querySelector('#copy-url-btn').addEventListener('click', () => this.copyToClipboard('#webhook-url-display'));
        this.container.querySelector('#copy-token-btn').addEventListener('click', () => this.copyToClipboard('#webhook-token-display'));
        this.container.querySelector('#wizard-next-btn').addEventListener('click', () => {
            this.currentStep = 2;
            this.updateWizardState();
        });
        this.container.querySelector('#wizard-finish-btn').addEventListener('click', this.handleFinish.bind(this));
        this.container.dataset.eventsAttached = 'true';
    }

    copyToClipboard(selector) {
        const input = this.container.querySelector(selector);
        navigator.clipboard.writeText(input.value).then(() => this.appClient.notifier.success("Copied!"));
    }

    async handleFinish() {
        const finishBtn = this.container.querySelector('#wizard-finish-btn');
        finishBtn.disabled = true;
        finishBtn.innerHTML = `<span class="spinner-border spinner-border-sm"></span> Saving...`;

        const workflowId = this.container.querySelector('#meta-workflow-select').value;

        const finalConfig = {
            provider: this.providerName,
            channelType: this.config.channelType,
            isEnabled: true,
            setupId: this.setupId,
            inboundWorkflowId: workflowId || null,  
            settings: {},
            credentials: {}
        };

        // Dynamically gather data based on the UI config
        this.uiConfig.fields.forEach(field => {
            const value = this.container.querySelector(`#meta-${field.id}`).value.trim();
            if (['accessToken', 'appSecret'].includes(field.id)) {
                finalConfig.credentials[field.id] = value;
            } else if (['pageId', 'phoneNumberId', 'wabaId'].includes(field.id)) {
                finalConfig.settings[field.id] = value;
            } else {
                finalConfig[field.id] = value;
            }
        });

        try {
            // No pre-validation needed for Meta channels as per our design
            await this.appClient.rpcClient.call('ChannelManagerRpc.create', finalConfig);
            this.appClient.notifier.success(`Channel '${finalConfig.name}' configured successfully!`);
            this.emit('channelSaved');
            this.modalInstance.hide();
        } catch (error) {
            this.appClient.notifier.error(`Setup failed: ${error.message}`);
        } finally {
            finishBtn.disabled = false;
            finishBtn.textContent = 'Finish Setup';
        }
    }

    render() { return this.container; }
    destroy() {
        this.appClient.events.server.off('meta:webhookVerified', this._boundWebhookVerifiedHandler);
        this.modalInstance?.dispose();
        this.container?.remove();
        super.destroy();
    }
}